import React from 'react'
import { parseISO, isToday, isYesterday, isWithinInterval, subDays } from 'date-fns'
import classNames from 'classnames'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import GridList from 'components/GridList'
import AskVisoInsertCharts from './components/content/AskVisoInsertCharts'
import IncidentPieChart from '../../screens/Incidents/IncidentAnalytics/charts/IncidentPieChart'
import IncidentCartesianChart from '../../screens/Incidents/IncidentAnalytics/charts/IncidentCartesianChart'
import { GROUP_BY_UNIQUE_COLORS } from '../../containers/CustomDashboard/Dashboard/details/charts/utils'

export const AskVisoDisclaimer = () => <div className='ask-viso__disclaimer'>The AI can make mistakes. Viso doesn't use your data to train its models.</div>

export const buildMessage = ({
  response = [], color, dashboardDetails, devicesData, toggleReloadDataOnClose, layout
}) => {
  let output = []

  if (response?.length > 0) {
    response.forEach((item, index) => {

      if (item.type === 'markdown') {
        output.push(<ReactMarkdown key={`markdown-${index}`} remarkPlugins={[remarkGfm]}>{item.content}</ReactMarkdown>)
      }

      if (item.type === 'table') {
        const tableData = item.table
        const gridProps = {
          color: color,
          transparentHead: true,
          isScrollable: true,
          isBordered: true,
          paginationProps: {
            enablePagination: true,
            pageLimit: 5,
            dropUp: true
          }
        }

        output.push(
          <div className='ask-viso-chat__table' key={`table-${index}`}>
            {tableData.title && <h3>{tableData.title}</h3>}
            <GridList gridDefs={tableData.definitions} gridData={tableData.data} gridProps={gridProps} />
            <div>
              <span className='viso-comment__meta'>
                <button
                  type='button'
                  className='viso-comment__button'
                  onClick={async (e) => {
                    const btn = e.currentTarget
                    const prevText = btn.textContent
                    try {
                      await copyTableToClipboard({ headers: tableData.definitions.map(d => d.column), rows: tableData.data, format: 'tsv' })
                      btn.textContent = 'Copied!'
                      setTimeout(() => { btn.textContent = prevText || 'Copy Table' }, 1200)
                    } catch (err) {
                      console.log('copy table failed', err)
                      btn.textContent = 'Copy failed'
                      setTimeout(() => { btn.textContent = prevText || 'Copy Table' }, 1500)
                    } finally {
                      btn.disabled = false
                      btn.removeAttribute('aria-busy')
                    }
                  }}
                >
                  Copy Table
                </button>
              </span>
            </div>
          </div>
        )
      }

      if (item.type === 'chart') {
        const chartItem = item.chart
        const chartType = chartItem.chartType
        const isPie = chartType === 'PieChart'
        const { output: chartData, defs: chartDef } = buildChartData(chartItem)
        const ChartTag = isPie ? IncidentPieChart : IncidentCartesianChart
        const typeProp = String(chartType).replace('Chart', '').toLowerCase()
        const chartId = `askVisoChart_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`
        output.push(
          <div className={classNames('viso-comment__chart', chartData.title && 'viso-comment__chart--title')} key={`chart-${index}`}>
            <ChartTag
              chartData={chartData}
              chartDef={{ ...chartDef, chartType: typeProp }}
              chartHeight={300}
              chartId={chartId}
              height={isPie ? 300 : 360}
              innerRadius={55}
              outerRadius={75}
              legendPosition={isPie ? 'right' : 'bottom'}
              isAnimationActive={false}
            />
            <div>
              <span className='viso-comment__meta'>
                <button
                  type='button'
                  className='viso-comment__button'
                  onClick={async (e) => {
                    const btn = e.currentTarget
                    const prevText = btn.textContent
                    try {
                      btn.disabled = true
                      btn.setAttribute('aria-busy', 'true')
                      btn.textContent = 'Generating...'
                      await downloadChartPngDom(document.getElementById(chartId))
                    } finally {
                      btn.disabled = false
                      btn.removeAttribute('aria-busy')
                      btn.textContent = prevText || 'Download Chart'
                    }
                  }}
                >
                  Download Chart
                </button>
              </span>
            </div>
          </div>
        )
      }

      if (item.type === 'visoChart' || item.type === 'reviewVisoChart' || item.type === 'recommendVisoChart' || item.type === 'improveVisoChart') {
        if (item.visoChart && item.visoChart.length > 0) {
          item.visoChart.forEach((visoChart, chartIndex) => {
            const layoutKey = (item.type === 'reviewVisoChart' || item.type === 'reviewVisoChart') ? 'id' : 'title'
            let findLayout = layout?.length > 0 && layout.find(l => l.i === visoChart[layoutKey])
            output.push(
              <div key={`visoChart=${visoChart.title}-${chartIndex}`}>
                <div className='ask-viso-markdown__build-chart'>
                  <AskVisoInsertCharts
                    chart={visoChart}
                    color={color}
                    dashboardDetails={dashboardDetails}
                    devicesData={devicesData}
                    toggleReloadDataOnClose={toggleReloadDataOnClose}
                    item={item}
                    layout={findLayout}
                  />
                </div>
              </div>
            )
          })
        }
      }

      if (item.type === 'links') {
        output.push(
          <div key={`links-${index}`}>
            <h3>Source Links</h3>
            <ul>
              {item.links.map((link, linkIndex) => (
                <li key={`${link.title}-${linkIndex}`}>
                  <a href={link.url} target='_blank' rel='noopener noreferrer'>{link.title}</a>
                </li>
              ))}
            </ul>
          </div>
        )
      }

    })
  }

  return output
}

const downloadChartPngDom = async (containerEl, { filename = 'chart', background, scale } = {}) => {
  try {
    if (!containerEl) return
    const { default: html2canvas } = await import('html2canvas')

    let bg = background
    if (bg === undefined || bg === null) {
      const cs = window.getComputedStyle(containerEl)
      const bgc = cs.getPropertyValue('background-color')
      if (bgc && bgc !== 'rgba(0, 0, 0, 0)' && bgc !== 'transparent') {
        bg = bgc
      } else {
        bg = '#ffffff'
      }
    }

    const canvas = await html2canvas(containerEl, {
      backgroundColor: bg,
      useCORS: true,
      allowTaint: false,
      scale: scale || Math.max(window.devicePixelRatio || 1, 1),
      logging: false,
      windowWidth: document.documentElement.clientWidth,
      windowHeight: document.documentElement.clientHeight,
    })

    await new Promise((resolve) => {
      canvas.toBlob((blob) => {
        if (!blob) return resolve()
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `${filename}.png`
        a.click()
        URL.revokeObjectURL(url)
        resolve()
      }, 'image/png')
    })
  } catch (e) {
    console.error('downloadChartPngDom error:', e?.message || e)
  }
}

export const filterByDateRange = (data) => {
  const now = new Date()
  const today = []
  const yesterday = []
  const previous7Days = []
  const previous30Days = []
  const older = []

  data.forEach(item => {
    const date = parseISO(item.lastMessageAt || item.createdAt)

    if (isToday(date)) {
      today.push(item)
    } else if (isYesterday(date)) {
      yesterday.push(item)
    } else if (
      isWithinInterval(date, {
        start: subDays(now, 7),
        end: subDays(now, 2)
      })
    ) {
      previous7Days.push(item)
    } else if (
      isWithinInterval(date, {
        start: subDays(now, 30),
        end: subDays(now, 8)
      })
    ) {
      previous30Days.push(item)
    } else {
      older.push(item)
    }
  })

  return {
    today: today.reverse(),
    yesterday: yesterday.reverse(),
    previous7Days: previous7Days.reverse(),
    previous30Days: previous30Days.reverse(),
    older: older.reverse()
  }
}

const toNumber = (v) => {
  const n = Number(v)
  return Number.isFinite(n) ? n : 0
}

const buildChartData = (input = {}, colors = GROUP_BY_UNIQUE_COLORS) => {
  const items = Array.isArray(input.data) ? input.data : []
  const chartType = input.chartType
  const isPieChart = chartType === 'PieChart'
  let effectiveItems = items
  let effectiveYKeys = Array.isArray(input.yKey) ? input.yKey.slice() : (input.yKey ? [input.yKey] : [])
  let effectiveStackKeys = Array.isArray(input.stackKeys) ? input.stackKeys.map(g => Array.isArray(g) ? g.slice() : [g]) : undefined
  const xKey = input.xKey || input.nameKey

  if (!isPieChart && effectiveStackKeys && effectiveStackKeys.length > 0 && xKey) {
    const flatStacks = effectiveStackKeys.flat()
    if (flatStacks.length === 1 && effectiveYKeys.length === 1) {
      const categoryKey = flatStacks[0]
      const valueKey = effectiveYKeys[0]
      if (categoryKey && valueKey) {
        const catSet = new Set()
        const grouped = new Map()
        for (const it of items) {
          const x = it[xKey]
          const cat = it[categoryKey]
          const val = toNumber(it[valueKey])
          if (x == null || cat == null) continue
          if (!grouped.has(x)) grouped.set(x, { [xKey]: x })
          const obj = grouped.get(x)
          obj[cat] = toNumber(obj[cat]) + val
          catSet.add(cat)
        }
        const categories = Array.from(catSet)
        if (categories.length) {
          effectiveItems = Array.from(grouped.values())
          effectiveYKeys = categories
          effectiveStackKeys = [categories]
        }
      }
    }
  }

  const output = effectiveItems.map((it, i) => ({
    ...it,
    name: xKey ? it[xKey] : it[input?.nameKey],
    value: toNumber(input.yKey ? it[input.yKey] : it[input.dataKey]),
    ...(isPieChart && { fill: colors[(i + 1) % colors.length] })
  }))
  const defs = {
    tooltipTitle: input.xKey,
    yDataKey: isPieChart ? 'value' : effectiveYKeys,
    stackKeys: effectiveStackKeys,
    disableXKey: effectiveStackKeys ? true : false,
    metrics:
      isPieChart ?
        output.map(d => ({
          dataKey: d.name,
          fill: d.fill
        })) :
        (effectiveStackKeys && effectiveStackKeys.length) ?
          effectiveStackKeys.flat().map((stack, i) => ({
            dataKey: stack,
            fill: colors[(i + 1) % colors.length]
          })) :
          effectiveYKeys.map((label, i) => ({
            dataKey: label,
            fill: colors[(i + 1) % colors.length]
          }))
  }

  return { output, defs }
}

const toText = (n) => {
  if (n == null) return ''
  if (typeof n === 'string') return n
  if (Array.isArray(n)) return n.map(toText).join('')
  if (typeof n === 'object') {
    if (n.props) return toText(n.props.children)
    if (typeof n.value === 'string') return n.value
    if (Array.isArray(n.children)) return n.children.map(toText).join('')
  }
  return String(n)
}

const buildTableString = ({ headers = [], rows = [], format = 'tsv' } = {}) => {
  const esc = (v, sep) => {
    const s = (v == null ? '' : String(v)).replace(/\r?\n/g, ' ')
    if (sep === ',') {
      const needsQuotes = /[",\n]/.test(s)
      const escaped = s.replace(/"/g, '""')
      return needsQuotes ? `"${escaped}"` : escaped
    }
    if (sep === '\t') return s.replace(/\t/g, ' ')
    return s
  }

  const sep = format === 'csv' ? ',' : '\t'
  const headLine = headers.map(h => esc(h, sep)).join(sep)
  const body = rows.map(r => headers.map(h => esc(r[h], sep)).join(sep)).join('\n')
  return [headLine, body].filter(Boolean).join('\n')
}

const writeToClipboard = async (text) => {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text)
      return true
    }
  } catch (_) { }

  try {
    const ta = document.createElement('textarea')
    ta.value = text
    ta.style.position = 'fixed'
    ta.style.top = '-1000px'
    ta.style.left = '-1000px'
    ta.setAttribute('readonly', 'true')
    document.body.appendChild(ta)
    ta.select()
    document.execCommand('copy')
    document.body.removeChild(ta)
    return true
  } catch (_) {
    return false
  }
}

export const copyTableToClipboard = async ({ headers = [], rows = [], format = 'markdown' } = {}) => {
  const text = buildTableString({ headers, rows, format })
  const ok = await writeToClipboard(text)
  if (!ok) throw new Error('Clipboard copy failed')
}